/**
 * <Dropdown> — Menu triggered by a button. Keyboard-navigable.
 *
 * Built on @radix-ui/react-dropdown-menu. Use for:
 *   - Action menus (Edit, Duplicate, Delete)
 *   - Option selection (Language, Theme)
 *   - Navigation links grouped under a trigger
 *
 * For multi-select or searchable options, compose with <Popover> instead.
 *
 *   <Dropdown.Root>
 *     <Dropdown.Trigger asChild>
 *       <button>Actions</button>
 *     </Dropdown.Trigger>
 *     <Dropdown.Content side="bottom" align="end">
 *       <Dropdown.Item onSelect={handleEdit}>Edit</Dropdown.Item>
 *       <Dropdown.Item onSelect={handleDuplicate}>Duplicate</Dropdown.Item>
 *       <Dropdown.Separator />
 *       <Dropdown.Item onSelect={handleDelete} danger>Delete</Dropdown.Item>
 *     </Dropdown.Content>
 *   </Dropdown.Root>
 *
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §4.4
 */

import React from 'react';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { useOverlayPortal } from './useOverlayPortal';

/**
 * Dropdown.Root
 */
function DropdownRoot({ open, defaultOpen, onOpenChange, modal = true, children }) {
  return (
    <RadixDropdown.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      {children}
    </RadixDropdown.Root>
  );
}
DropdownRoot.displayName = 'Dropdown.Root';

const DropdownTrigger = RadixDropdown.Trigger;

/**
 * Dropdown.Content — menu container.
 */
function DropdownContent({
  children,
  side = 'bottom',
  align = 'end',
  sideOffset = 4,
  alignOffset = 0,
  collisionPadding = 8,
  className = '',
  ariaLabel,
  ariaLabelledBy,
  ...rest
}) {
  const portalContainer = useOverlayPortal();

  if (process.env.NODE_ENV !== 'production') {
    if (!ariaLabel && !ariaLabelledBy) {
      console.warn(
        '[Dropdown.Content] No ariaLabel or ariaLabelledBy. ' +
        'Screen readers require an accessible name. ' +
        'Provide ariaLabel or ariaLabelledBy pointing to a visible label.'
      );
    }
  }

  return (
    <RadixDropdown.Portal container={portalContainer}>
      <RadixDropdown.Content
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={`min-w-[180px] max-w-[320px] bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] border border-[var(--aif-border-default)] rounded-[var(--aif-radius-md)] shadow-[var(--aif-shadow-lg)] py-1 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 ${className}`}
        style={{ zIndex: 'var(--aif-z-contextual)' }}
        {...rest}
      >
        {children}
      </RadixDropdown.Content>
    </RadixDropdown.Portal>
  );
}
DropdownContent.displayName = 'Dropdown.Content';

/**
 * Dropdown.Item — clickable menu entry.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {() => void} [props.onSelect]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.danger] - red styling for destructive actions
 * @param {React.ReactNode} [props.leadingIcon]
 * @param {React.ReactNode} [props.trailing] - trailing shortcut or icon
 */
function DropdownItem({
  children,
  onSelect,
  disabled = false,
  danger = false,
  leadingIcon,
  trailing,
  className = '',
  ...rest
}) {
  const baseClasses =
    'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none outline-none ' +
    'data-[highlighted]:bg-[var(--aif-bg-tertiary)] data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed ' +
    'rounded-[var(--aif-radius-sm)] mx-1 ';
  const colorClasses = danger
    ? 'text-red-600 dark:text-red-400 data-[highlighted]:bg-red-50 dark:data-[highlighted]:bg-red-900/20'
    : 'text-[var(--aif-text-primary)]';

  return (
    <RadixDropdown.Item
      onSelect={onSelect}
      disabled={disabled}
      className={`${baseClasses} ${colorClasses} ${className}`}
      {...rest}
    >
      {leadingIcon && <span className="flex-shrink-0 w-4 h-4">{leadingIcon}</span>}
      <span className="flex-1">{children}</span>
      {trailing && <span className="text-[var(--aif-text-muted)] text-xs">{trailing}</span>}
    </RadixDropdown.Item>
  );
}
DropdownItem.displayName = 'Dropdown.Item';

/**
 * Dropdown.CheckboxItem — toggleable menu entry.
 */
function DropdownCheckboxItem({
  children,
  checked,
  onCheckedChange,
  disabled = false,
  className = '',
  ...rest
}) {
  return (
    <RadixDropdown.CheckboxItem
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none outline-none data-[highlighted]:bg-[var(--aif-bg-tertiary)] data-[disabled]:opacity-50 rounded-[var(--aif-radius-sm)] mx-1 text-[var(--aif-text-primary)] ${className}`}
      {...rest}
    >
      <RadixDropdown.ItemIndicator className="flex-shrink-0 w-4 h-4">
        <svg viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 text-[var(--aif-color-primary)]">
          <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
        </svg>
      </RadixDropdown.ItemIndicator>
      <span className="flex-1 ml-5">{children}</span>
    </RadixDropdown.CheckboxItem>
  );
}
DropdownCheckboxItem.displayName = 'Dropdown.CheckboxItem';

/**
 * Dropdown.RadioGroup + Dropdown.RadioItem — mutually exclusive selection.
 */
const DropdownRadioGroup = RadixDropdown.RadioGroup;

function DropdownRadioItem({ children, value, disabled = false, className = '', ...rest }) {
  return (
    <RadixDropdown.RadioItem
      value={value}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none outline-none rounded-[var(--aif-radius-sm)] mx-1 text-[var(--aif-text-primary)] data-[highlighted]:bg-[var(--aif-bg-tertiary)] data-[state=checked]:bg-[var(--aif-color-primary)]/10 data-[state=checked]:text-[var(--aif-color-primary)] data-[disabled]:opacity-50 ${className}`}
      {...rest}
    >
      <span className="flex-1 min-w-0">{children}</span>
      <RadixDropdown.ItemIndicator className="flex-shrink-0 ml-auto">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 text-[var(--aif-color-primary)]">
          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </RadixDropdown.ItemIndicator>
    </RadixDropdown.RadioItem>
  );
}
DropdownRadioItem.displayName = 'Dropdown.RadioItem';

/**
 * Dropdown.Separator — horizontal divider.
 */
function DropdownSeparator({ className = '' }) {
  return (
    <RadixDropdown.Separator
      className={`h-px bg-[var(--aif-border-default)] my-1 mx-2 ${className}`}
    />
  );
}
DropdownSeparator.displayName = 'Dropdown.Separator';

/**
 * Dropdown.Label — non-clickable section header.
 */
function DropdownLabel({ children, className = '' }) {
  return (
    <RadixDropdown.Label
      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--aif-text-muted)] ${className}`}
    >
      {children}
    </RadixDropdown.Label>
  );
}
DropdownLabel.displayName = 'Dropdown.Label';

/**
 * Dropdown.Sub / SubTrigger / SubContent — nested menus.
 */
const DropdownSub = RadixDropdown.Sub;

function DropdownSubTrigger({ children, className = '', ...rest }) {
  return (
    <RadixDropdown.SubTrigger
      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none outline-none data-[highlighted]:bg-[var(--aif-bg-tertiary)] data-[state=open]:bg-[var(--aif-bg-tertiary)] rounded-[var(--aif-radius-sm)] mx-1 text-[var(--aif-text-primary)] ${className}`}
      {...rest}
    >
      <span className="flex-1">{children}</span>
      <svg viewBox="0 0 8 12" fill="currentColor" className="w-2 h-3 text-[var(--aif-text-muted)]">
        <path d="M0 0l8 6-8 6z" />
      </svg>
    </RadixDropdown.SubTrigger>
  );
}
DropdownSubTrigger.displayName = 'Dropdown.SubTrigger';

function DropdownSubContent({ children, className = '', ...rest }) {
  const portalContainer = useOverlayPortal();
  return (
    <RadixDropdown.Portal container={portalContainer}>
      <RadixDropdown.SubContent
        sideOffset={2}
        alignOffset={-4}
        className={`min-w-[180px] max-w-[320px] bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] border border-[var(--aif-border-default)] rounded-[var(--aif-radius-md)] shadow-[var(--aif-shadow-lg)] py-1 focus:outline-none ${className}`}
        style={{ zIndex: 'var(--aif-z-contextual)' }}
        {...rest}
      >
        {children}
      </RadixDropdown.SubContent>
    </RadixDropdown.Portal>
  );
}
DropdownSubContent.displayName = 'Dropdown.SubContent';

// Public API
export const Dropdown = {
  Root: DropdownRoot,
  Trigger: DropdownTrigger,
  Content: DropdownContent,
  Item: DropdownItem,
  CheckboxItem: DropdownCheckboxItem,
  RadioGroup: DropdownRadioGroup,
  RadioItem: DropdownRadioItem,
  Separator: DropdownSeparator,
  Label: DropdownLabel,
  Sub: DropdownSub,
  SubTrigger: DropdownSubTrigger,
  SubContent: DropdownSubContent,
};
